/**
 * Shared, cross-component cache of SkyDent damage-record status, keyed by Aether
 * event id.
 *
 * The Damage Location lives in SkyDent (not on the Aether event), so several
 * views need the same per-event answer to "does a correlating SkyDent record
 * exist, and is its Damage Location still blank?": the homepage/History IROP
 * flag, the IROP-section SkyDent affordance, and the event modal's save gate.
 *
 * Kept as a module-level store (not React state) so that a save in the SkyDent
 * modal can invalidate the entry and every subscribed view updates together,
 * and so each event is fetched once rather than once per consuming component.
 */
import { lookupSkyDentRecord } from './skyDentService';

/** SkyDent damage status for a single Aether event. */
export interface SkyDentDamageStatus {
  /** True when a correlating SkyDent record exists. */
  exists: boolean;
  /** True when the SkyDent Damage Location is blank. */
  locationMissing: boolean;
}

type Listener = () => void;

const statusByEventId = new Map<string, SkyDentDamageStatus>();
const inFlight = new Set<string>();
const listeners = new Set<Listener>();
let storeVersion = 0;

function notifyListeners(): void {
  storeVersion += 1;
  listeners.forEach((listener) => listener());
}

/**
 * A monotonically increasing version, bumped on every status change. Used as the
 * `useSyncExternalStore` snapshot so subscribers reliably re-render on updates.
 */
export function getSkyDentStoreVersion(): number {
  return storeVersion;
}

/** Subscribes to status changes; returns an unsubscribe function. */
export function subscribeSkyDentDamageStatus(listener: Listener): () => void {
  listeners.add(listener);
  return (): void => {
    listeners.delete(listener);
  };
}

/** Returns the cached status for an event, or undefined if not yet resolved. */
export function getSkyDentDamageStatus(eventId: string): SkyDentDamageStatus | undefined {
  return statusByEventId.get(eventId);
}

async function fetchStatus(eventId: string): Promise<void> {
  inFlight.add(eventId);
  try {
    const view = await lookupSkyDentRecord(eventId);
    statusByEventId.set(eventId, {
      exists: view.exists,
      locationMissing: view.editable.damage_location.trim().length === 0,
    });
  } catch {
    // Leave unresolved so a later ensure/refresh retries the lookup.
  } finally {
    inFlight.delete(eventId);
    notifyListeners();
  }
}

/** Loads the status for an event once; a no-op if already cached or in flight. */
export function ensureSkyDentDamageStatus(eventId: string): void {
  if (!eventId || statusByEventId.has(eventId) || inFlight.has(eventId)) return;
  void fetchStatus(eventId);
}

/**
 * Forces a re-fetch of the event's status (e.g. after a SkyDent save, or when
 * the SkyDent affordance opens and needs current data). The cached value is kept
 * until the fresh result arrives, so subscribers never briefly see "unknown".
 */
export function refreshSkyDentDamageStatus(eventId: string): void {
  if (!eventId || inFlight.has(eventId)) return;
  void fetchStatus(eventId);
}
